import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../App";
import { Users, Shield, Trash2, UserPlus, KeyRound, Eye, EyeOff, CheckCircle, Building2, Pencil, Lightbulb, Check, X, ChevronDown, ChevronUp, User, Crown, Trophy, Medal, Award, Calendar, FileText, Clock, Camera, Mail, Plus, Upload, ExternalLink, UserCheck, Link } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import PiiWarningBanner from "../components/PiiWarningBanner";

// Role icons and colors - more visible
const ROLES = {
  super_admin: { icon: Shield, color: "text-rose-300", bg: "bg-rose-600", border: "border-rose-500", label: "Super Admin", hoverBg: "hover:bg-rose-500/20", hoverBorder: "hover:border-rose-500", inactiveText: "text-rose-400/60" },
  admin: { icon: Shield, color: "text-purple-300", bg: "bg-purple-600", border: "border-purple-500", label: "Admin", hoverBg: "hover:bg-purple-500/20", hoverBorder: "hover:border-purple-500", inactiveText: "text-purple-400/60" },
  manager: { icon: Crown, color: "text-amber-300", bg: "bg-amber-600", border: "border-amber-500", label: "Manager", hoverBg: "hover:bg-amber-500/20", hoverBorder: "hover:border-amber-500", inactiveText: "text-amber-400/60" },
  user: { icon: User, color: "text-indigo-300", bg: "bg-indigo-600", border: "border-indigo-500", label: "User", hoverBg: "hover:bg-indigo-500/20", hoverBorder: "hover:border-indigo-500", inactiveText: "text-indigo-400/60" },
};

const DEPARTMENTS = {
  sales: { label: "Sales", color: "bg-indigo-600" },
  csm: { label: "Customer Success", color: "bg-emerald-600" },
  dev: { label: "Development", color: "bg-purple-600" },
  support: { label: "Support", color: "bg-amber-600" },
  marketing: { label: "Marketing", color: "bg-pink-600" },
};

// Old ROLES array for backward compatibility with role dropdown
const ROLES_DROPDOWN = [
  { value: "admin", label: "Admin" },
  { value: "sales", label: "Sales" },
  { value: "csm", label: "CSM" },
  { value: "dev", label: "Dev" },
  { value: "support", label: "Support" },
  { value: "marketing", label: "Marketing" },
];

const AdminPanel = () => {
  const { user, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [rockIdeas, setRockIdeas] = useState([]);
  const [showIdeasPanel, setShowIdeasPanel] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [piiReport, setPiiReport] = useState(null);
  const [uploadingUserId, setUploadingUserId] = useState(null);
  const [pendingDeptChanges, setPendingDeptChanges] = useState({});
  const [savingUserId, setSavingUserId] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [editNotes, setEditNotes] = useState("");
  const [editName, setEditName] = useState("");
  const [editJoinDate, setEditJoinDate] = useState("");
  const [editDepartment, setEditDepartment] = useState("sales");
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [savingUserEdit, setSavingUserEdit] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const fileInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const [organizations, setOrganizations] = useState([]);
  const [accounts, setAccounts] = useState([]);
  
  // Super Admin - Org visibility management
  const [isOrgVisibilityDialogOpen, setIsOrgVisibilityDialogOpen] = useState(false);
  const [selectedUserForOrgs, setSelectedUserForOrgs] = useState(null);
  const [allOrgsForVisibility, setAllOrgsForVisibility] = useState([]);
  const [savingOrgVisibility, setSavingOrgVisibility] = useState(false);
  
  // New Organization/Account dialogs
  const [isNewOrgDialogOpen, setIsNewOrgDialogOpen] = useState(false);
  const [isNewAccountDialogOpen, setIsNewAccountDialogOpen] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: "", calldrip_org_id: "", slug: "" });
  const [newAccount, setNewAccount] = useState({ name: "", calldrip_account_id: "", slug: "" });
  const [savingOrg, setSavingOrg] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [activeTab, setActiveTab] = useState("internal"); // "internal" or "external"
  
  // Report upload state
  const [isUploadReportDialogOpen, setIsUploadReportDialogOpen] = useState(false);
  const [uploadReportOrg, setUploadReportOrg] = useState("");
  const [uploadReportAccount, setUploadReportAccount] = useState("");
  const [uploadReportTitle, setUploadReportTitle] = useState("Dashboard Report");
  const [uploadReportPeriod, setUploadReportPeriod] = useState("");
  const [uploadReportType, setUploadReportType] = useState("auto"); // auto, kubota, standalone_inbound, standalone_outbound, calldrip_scorecard
  const [uploadReportTheme, setUploadReportTheme] = useState("dark"); // dark or light
  
  // Additional file refs for scorecard report
  const scoredMetricsFileRef = useRef(null);
  const customerHappinessFileRef = useRef(null);
  const activitiesFileRef = useRef(null);
  const [activitiesFile, setActivitiesFile] = useState(null);
  const [scoredMetricsFile, setScoredMetricsFile] = useState(null);
  const [customerHappinessFile, setCustomerHappinessFile] = useState(null);
  const [uploadingReport, setUploadingReport] = useState(false);
  const reportFileInputRef = useRef(null);
  
  // Email report upload state
  const [isUploadEmailReportDialogOpen, setIsUploadEmailReportDialogOpen] = useState(false);
  const [uploadEmailReportOrg, setUploadEmailReportOrg] = useState("");
  const [uploadEmailReportAccount, setUploadEmailReportAccount] = useState("");
  const [uploadingEmailReport, setUploadingEmailReport] = useState(false);
  const emailReportFileInputRef = useRef(null);
  
  // Manage reports state
  const [isManageReportsDialogOpen, setIsManageReportsDialogOpen] = useState(false);
  const [manageReportsAccount, setManageReportsAccount] = useState(null);
  const [accountReports, setAccountReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [deletingReportId, setDeletingReportId] = useState(null);
  
  // Logo upload state
  const [isUploadLogoDialogOpen, setIsUploadLogoDialogOpen] = useState(false);
  const [uploadLogoAccount, setUploadLogoAccount] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoFileInputRef = useRef(null);
  
  // Scored Report upload state
  const [isScoredReportDialogOpen, setIsScoredReportDialogOpen] = useState(false);
  const [scoredReportOrg, setScoredReportOrg] = useState("");
  const [scoredReportAccount, setScoredReportAccount] = useState("");
  const [scoredReportPeriod, setScoredReportPeriod] = useState("");
  const [scoredReportDepartment, setScoredReportDepartment] = useState("sales");
  const [uploadingScoredReport, setUploadingScoredReport] = useState(false);
  const [scoredReportResult, setScoredReportResult] = useState(null);
  const scoredReportFileInputRef = useRef(null);
  
  // Edit dialog logo upload
  const [uploadingEditLogo, setUploadingEditLogo] = useState(false);
  const editLogoInputRef = useRef(null);
  
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "sales",
    department: "sales",
    is_external: false,
    calldrip_org_id: "",
    calldrip_account_id: "",
    calldrip_user_id: "",
    external_role: "user",
  });
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, ideasRes, orgsRes, accountsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/rock-ideas`, { headers }),
        axios.get(`${API}/external/organizations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/external/accounts`, { headers }).catch(() => ({ data: [] }))
      ]);
      setUsers(usersRes.data);
      setRockIdeas(ideasRes.data);
      setOrganizations(orgsRes.data || []);
      setAccounts(accountsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateIdeaStatus = async (ideaId, status) => {
    try {
      await axios.put(`${API}/rock-ideas/${ideaId}/status?status=${status}`, {}, { headers });
      fetchData();
    } catch (error) {
      console.error("Error updating idea status:", error);
    }
  };

  const handleAvatarClick = (userId) => {
    setUploadingUserId(userId);
    fileInputRef.current?.click();
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingUserId) return;

    if (!file.type.startsWith("image/")) {
      setSuccessMessage("Please select an image file");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      await axios.post(`${API}/admin/users/${uploadingUserId}/avatar`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });

      // Refresh users list
      fetchData();
      setSuccessMessage("Profile picture updated!");
    } catch (error) {
      setSuccessMessage("Failed to upload image");
    } finally {
      setUploadingUserId(null);
      e.target.value = "";
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email) {
      alert("Please fill in name and email");
      return;
    }
    
    if (newUser.is_external && (!newUser.calldrip_org_id || !newUser.calldrip_account_id)) {
      alert("Please fill in Organization and Account for external users");
      return;
    }

    try {
      await axios.post(`${API}/admin/users`, newUser, { headers });
      setNewUser({ 
        name: "", 
        email: "", 
        password: "", 
        role: "sales", 
        department: "sales",
        is_external: false,
        calldrip_org_id: "",
        calldrip_account_id: "",
        calldrip_user_id: "",
        external_role: "user"
      });
      setShowNewUserPassword(false);
      setIsAddDialogOpen(false);
      setSuccessMessage(`User "${newUser.name}" created successfully! Welcome email sent.`);
      setTimeout(() => setSuccessMessage(""), 4000);
      fetchData();
    } catch (error) {
      console.error("Error adding user:", error);
      alert(error.response?.data?.detail || "Failed to add user");
    }
  };

  // Create new organization
  const handleCreateOrganization = async () => {
    if (!newOrg.name) {
      alert("Please enter an organization name");
      return;
    }
    
    setSavingOrg(true);
    try {
      // Auto-generate slug and ID if not provided
      const slug = newOrg.slug || newOrg.name.toLowerCase().replace(/[^a-z0-9]/g, '');
      const calldrip_org_id = newOrg.calldrip_org_id || `ORG-${slug.toUpperCase()}-${Date.now().toString(36).toUpperCase()}`;
      
      await axios.post(`${API}/external/organizations`, {
        name: newOrg.name,
        slug: slug,
        calldrip_org_id: calldrip_org_id
      }, { headers });
      
      // Refresh organizations list
      const orgsRes = await axios.get(`${API}/external/organizations`, { headers });
      setOrganizations(orgsRes.data);
      
      // Auto-select the new org
      setNewUser({ ...newUser, calldrip_org_id: calldrip_org_id, calldrip_account_id: "" });
      
      setNewOrg({ name: "", calldrip_org_id: "", slug: "" });
      setIsNewOrgDialogOpen(false);
      setSuccessMessage(`Organization "${newOrg.name}" created!`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error creating organization:", error);
      alert(error.response?.data?.detail || "Failed to create organization");
    } finally {
      setSavingOrg(false);
    }
  };

  // Create new account
  const handleCreateAccount = async () => {
    if (!newAccount.name) {
      alert("Please enter an account name");
      return;
    }
    if (!newUser.calldrip_org_id) {
      alert("Please select an organization first");
      return;
    }
    
    setSavingAccount(true);
    try {
      // Auto-generate slug and ID if not provided
      const slug = newAccount.slug || newAccount.name.toLowerCase().replace(/[^a-z0-9]/g, '');
      const calldrip_account_id = newAccount.calldrip_account_id || `ACC-${slug.toUpperCase()}-${Date.now().toString(36).toUpperCase()}`;
      
      await axios.post(`${API}/external/accounts`, {
        name: newAccount.name,
        slug: slug,
        calldrip_org_id: newUser.calldrip_org_id,
        calldrip_account_id: calldrip_account_id
      }, { headers });
      
      // Refresh accounts list
      const accountsRes = await axios.get(`${API}/external/accounts`, { headers });
      setAccounts(accountsRes.data);
      
      // Auto-select the new account
      setNewUser({ ...newUser, calldrip_account_id: calldrip_account_id });
      
      setNewAccount({ name: "", calldrip_account_id: "", slug: "" });
      setIsNewAccountDialogOpen(false);
      setSuccessMessage(`Account "${newAccount.name}" created!`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error creating account:", error);
      alert(error.response?.data?.detail || "Failed to create account");
    } finally {
      setSavingAccount(false);
    }
  };

  // Upload report file
  const handleUploadReport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!uploadReportOrg || !uploadReportAccount) {
      alert("Please select an organization and account first");
      return;
    }
    
    // Find the org and account slugs
    const org = organizations.find(o => o.calldrip_org_id === uploadReportOrg);
    const account = accounts.find(a => a.calldrip_account_id === uploadReportAccount);
    
    if (!org || !account) {
      alert("Organization or account not found");
      return;
    }
    
    // Handle Calldrip Scorecard type separately (multi-file upload)
    if (uploadReportType === "calldrip_scorecard") {
      await handleScorecardUpload(file, org, account);
      return;
    }
    
    // Determine if this is a data file (CSV/Excel) or HTML file
    const isDataFile = file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    const isHtmlFile = file.name.endsWith('.html') || file.name.endsWith('.htm');
    
    if (!isDataFile && !isHtmlFile) {
      alert("Please upload a CSV, Excel (.xlsx, .xls), or HTML file");
      return;
    }
    
    setUploadingReport(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", uploadReportTitle || "Dashboard Report");
      formData.append("period", uploadReportPeriod || "current");
      formData.append("report_type", uploadReportType || "auto");
      
      // Use different endpoint for data files vs HTML files
      const endpoint = isDataFile 
        ? `${API}/external/reports/${org.slug}/${account.slug}/generate-dashboard`
        : `${API}/external/reports/${org.slug}/${account.slug}/upload`;
      
      const response = await axios.post(
        endpoint,
        formData,
        { 
          headers: { 
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const message = isDataFile 
        ? `Report generated from ${file.name}! The data has been analyzed and a dashboard was created.`
        : `Report uploaded for ${account.name}!`;
      
      setSuccessMessage(`${message} View at: /reports/${org.slug}/${account.slug}`);
      // Surface PII scan result (if backend returned it) — banner stays until user dismisses
      if (response.data && response.data.pii_report) {
        setPiiReport(response.data.pii_report);
      }
      setTimeout(() => setSuccessMessage(""), 6000);
      setIsUploadReportDialogOpen(false);
      setUploadReportOrg("");
      setUploadReportAccount("");
      setUploadReportTitle("Dashboard Report");
      setUploadReportPeriod("");
      setUploadReportType("auto");
    } catch (error) {
      console.error("Error uploading report:", error);
      alert(error.response?.data?.detail || "Failed to upload/generate report");
    } finally {
      setUploadingReport(false);
      if (reportFileInputRef.current) {
        reportFileInputRef.current.value = "";
      }
    }
  };

  // Handle Calldrip Scorecard multi-file upload
  const handleScorecardUpload = async (activitiesFile, org, account) => {
    setUploadingReport(true);
    try {
      const formData = new FormData();
      formData.append("activities_file", activitiesFile);
      formData.append("title", uploadReportTitle || "Scorecard Report");
      formData.append("period", uploadReportPeriod || "current");
      formData.append("theme", uploadReportTheme || "dark");
      
      // Add optional files if they exist
      if (scoredMetricsFile) {
        formData.append("scored_metrics_file", scoredMetricsFile);
      }
      if (customerHappinessFile) {
        formData.append("customer_happiness_file", customerHappinessFile);
      }
      
      const response = await axios.post(
        `${API}/external/reports/${org.slug}/${account.slug}/generate-scorecard`,
        formData,
        { 
          headers: { 
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const kpis = response.data.kpis_generated;
      let summary = "Generated: ";
      if (kpis.outbound) summary += "Outbound KPIs, ";
      if (kpis.inbound) summary += "Inbound KPIs, ";
      if (kpis.scored_metrics) summary += "Scored Metrics, ";
      if (kpis.customer_happiness) summary += "Customer Happiness, ";
      summary = summary.replace(/, $/, "");
      
      setSuccessMessage(`Scorecard Report generated! ${summary}. View at: /reports/${org.slug}/${account.slug}`);
      if (response.data && response.data.pii_report) {
        setPiiReport(response.data.pii_report);
      }
      setTimeout(() => setSuccessMessage(""), 8000);
      setIsUploadReportDialogOpen(false);
      resetUploadForm();
    } catch (error) {
      console.error("Error generating scorecard report:", error);
      alert(error.response?.data?.detail || "Failed to generate scorecard report");
    } finally {
      setUploadingReport(false);
      if (reportFileInputRef.current) reportFileInputRef.current.value = "";
    }
  };
  
  const resetUploadForm = () => {
    setUploadReportOrg("");
    setUploadReportAccount("");
    setUploadReportTitle("Dashboard Report");
    setUploadReportPeriod("");
    setUploadReportType("auto");
    setUploadReportTheme("dark");
    setActivitiesFile(null);
    setScoredMetricsFile(null);
    setCustomerHappinessFile(null);
  };

  // Upload email report file
  const handleUploadEmailReport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!uploadEmailReportOrg || !uploadEmailReportAccount) {
      alert("Please select an organization and account first");
      return;
    }
    
    // Find the org and account slugs
    const org = organizations.find(o => o.calldrip_org_id === uploadEmailReportOrg);
    const account = accounts.find(a => a.calldrip_account_id === uploadEmailReportAccount);
    
    if (!org || !account) {
      alert("Organization or account not found");
      return;
    }
    
    if (!file.name.endsWith('.html') && !file.name.endsWith('.htm')) {
      alert("Please upload an HTML file for the email report");
      return;
    }
    
    setUploadingEmailReport(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      await axios.post(
        `${API}/external/reports/${org.slug}/${account.slug}/upload-email`,
        formData,
        { 
          headers: { 
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setSuccessMessage(`Email report uploaded for ${account.name}! This will be used when "Copy Email" is clicked.`);
      setTimeout(() => setSuccessMessage(""), 6000);
      setIsUploadEmailReportDialogOpen(false);
      setUploadEmailReportOrg("");
      setUploadEmailReportAccount("");
    } catch (error) {
      console.error("Error uploading email report:", error);
      alert(error.response?.data?.detail || "Failed to upload email report");
    } finally {
      setUploadingEmailReport(false);
      if (emailReportFileInputRef.current) {
        emailReportFileInputRef.current.value = "";
      }
    }
  };

  // Generate Scored Calls Report from Excel
  const handleGenerateScoredReport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!scoredReportOrg || !scoredReportAccount) {
      alert("Please select an organization and account first");
      return;
    }
    
    const org = organizations.find(o => o.calldrip_org_id === scoredReportOrg);
    const account = accounts.find(a => a.calldrip_account_id === scoredReportAccount);
    
    if (!org || !account) {
      alert("Organization or account not found");
      return;
    }
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      alert("Please upload an Excel file (.xlsx or .xls)");
      return;
    }
    
    setUploadingScoredReport(true);
    setScoredReportResult(null);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", `${scoredReportDepartment.charAt(0).toUpperCase() + scoredReportDepartment.slice(1)} Scored Calls Report`);
      formData.append("period", scoredReportPeriod || "Current");
      formData.append("department", scoredReportDepartment);
      
      const response = await axios.post(
        `${API}/external/reports/${org.slug}/${account.slug}/generate-scored`,
        formData,
        { 
          headers: { 
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setScoredReportResult(response.data);
      setSuccessMessage(`Scorecard Report uploaded! Now available in client's report dropdown.`);
      setTimeout(() => setSuccessMessage(""), 6000);
    } catch (error) {
      console.error("Error uploading scored report:", error);
      alert(error.response?.data?.detail || "Failed to upload scored report");
    } finally {
      setUploadingScoredReport(false);
      if (scoredReportFileInputRef.current) {
        scoredReportFileInputRef.current.value = "";
      }
    }
  };


  // Load reports for an account (for manage dialog)
  const loadAccountReports = async (account) => {
    if (!account) return;
    
    setManageReportsAccount(account);
    setIsManageReportsDialogOpen(true);
    setLoadingReports(true);
    
    try {
      // Find the org for this account
      const org = organizations.find(o => o.calldrip_org_id === account.calldrip_org_id);
      if (!org) {
        alert("Organization not found for this account");
        return;
      }
      
      const response = await axios.get(
        `${API}/external/reports/${org.slug}/${account.slug}/list`,
        { headers }
      );
      setAccountReports(response.data.reports || []);
    } catch (error) {
      console.error("Error loading reports:", error);
      setAccountReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  // Delete a report
  const handleDeleteReport = async (reportId) => {
    if (!confirm("Are you sure you want to delete this report? This cannot be undone.")) {
      return;
    }
    
    setDeletingReportId(reportId);
    try {
      await axios.delete(`${API}/external/reports/${reportId}`, { headers });
      setAccountReports(prev => prev.filter(r => r.id !== reportId));
      setSuccessMessage("Report deleted successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting report:", error);
      alert(error.response?.data?.detail || "Failed to delete report");
    } finally {
      setDeletingReportId(null);
    }
  };

  // Set report as current
  const handleSetCurrentReport = async (reportId) => {
    try {
      await axios.put(`${API}/external/reports/${reportId}?is_current=true`, {}, { headers });
      setAccountReports(prev => prev.map(r => ({
        ...r,
        is_current: r.id === reportId
      })));
      setSuccessMessage("Report set as current");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error updating report:", error);
      alert(error.response?.data?.detail || "Failed to update report");
    }
  };

  // Upload client logo
  const handleUploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!uploadLogoAccount) {
      alert("Please select an account first");
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert("Please select an image file (PNG, JPG, etc.)");
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(
        `${API}/external/accounts/${uploadLogoAccount}/logo`,
        formData,
        { 
          headers: { 
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const account = accounts.find(a => a.calldrip_account_id === uploadLogoAccount);
      setSuccessMessage(`Logo uploaded for ${account?.name || 'account'}!`);
      setTimeout(() => setSuccessMessage(""), 4000);
      setIsUploadLogoDialogOpen(false);
      setUploadLogoAccount("");
      
      // Refresh accounts list to get updated logo_url
      const accountsRes = await axios.get(`${API}/external/accounts`, { headers });
      setAccounts(accountsRes.data);
    } catch (error) {
      console.error("Error uploading logo:", error);
      alert(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = "";
      }
    }
  };

  // Upload logo from edit dialog (for external users)
  const handleUploadLogoFromEdit = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !editingUser?.calldrip_account_id) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert("Please select an image file (PNG, JPG, etc.)");
      return;
    }
    
    setUploadingEditLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      await axios.post(
        `${API}/external/accounts/${editingUser.calldrip_account_id}/logo`,
        formData,
        { 
          headers: { 
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setSuccessMessage("Account logo updated!");
      setTimeout(() => setSuccessMessage(""), 4000);
      
      // Refresh accounts list to get updated logo_url
      const accountsRes = await axios.get(`${API}/external/accounts`, { headers });
      setAccounts(accountsRes.data);
    } catch (error) {
      console.error("Error uploading logo:", error);
      alert(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingEditLogo(false);
      if (editLogoInputRef.current) {
        editLogoInputRef.current.value = "";
      }
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { is_active: !isActive }, { headers });
      fetchData();
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { role: newRole }, { headers });
      setSuccessMessage(`Role updated to ${ROLES[newRole]?.label || newRole}`);
      setTimeout(() => setSuccessMessage(""), 2000);
      fetchData();
    } catch (error) {
      console.error("Error updating role:", error);
    }
  };

  // Stage department change (doesn't save immediately)
  const handleDepartmentChange = (userId, department) => {
    setPendingDeptChanges(prev => ({
      ...prev,
      [userId]: department
    }));
  };

  // Save the pending department change
  const handleSaveDepartment = async (userId) => {
    const newDept = pendingDeptChanges[userId];
    if (!newDept) return;
    
    setSavingUserId(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}`, { department: newDept, role: newDept }, { headers });
      setPendingDeptChanges(prev => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
      setSuccessMessage("Department updated!");
      setTimeout(() => setSuccessMessage(""), 3000);
      fetchData();
    } catch (error) {
      console.error("Error updating department:", error);
    } finally {
      setSavingUserId(null);
    }
  };

  // Cancel pending department change
  const handleCancelDeptChange = (userId) => {
    setPendingDeptChanges(prev => {
      const updated = { ...prev };
      delete updated[userId];
      return updated;
    });
  };

  const handleUpdateDepartment = async (userId, department) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { department, role: department }, { headers });
      fetchData();
    } catch (error) {
      console.error("Error updating department:", error);
    }
  };

  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  
  const handleDeleteUser = async (userId) => {
    setDeleteConfirmUser(userId);
  };

  const confirmDeleteUser = async () => {
    if (!deleteConfirmUser) return;
    
    try {
      await axios.delete(`${API}/admin/users/${deleteConfirmUser}`, { headers });
      setDeleteConfirmUser(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting user:", error);
      alert(error.response?.data?.detail || "Failed to delete user");
    }
  };

  // Impersonate user
  const handleImpersonate = async (user) => {
    try {
      const response = await axios.post(`${API}/admin/impersonate/${user.id}`, {}, { headers });
      
      // Store the original admin token so we can return
      const currentToken = localStorage.getItem("token");
      const currentUser = localStorage.getItem("user");
      localStorage.setItem("admin_token_backup", currentToken);
      localStorage.setItem("admin_user_backup", currentUser);
      
      // Set the impersonation token and user
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      localStorage.setItem("impersonating", "true");
      localStorage.setItem("impersonated_by", response.data.original_admin);
      
      // Redirect to home - they'll see whatever that user would see
      window.location.href = "/";
    } catch (error) {
      console.error("Error impersonating user:", error);
      alert(error.response?.data?.detail || "Failed to impersonate user");
    }
  };

  const [sendingPasswordReset, setSendingPasswordReset] = useState(null);
  const [resettingToDefault, setResettingToDefault] = useState(null);
  
  const handleSendPasswordResetEmail = async (u) => {
    setSendingPasswordReset(u.id);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: u.email });
      setSuccessMessage(`Password reset email sent to ${u.email}`);
      setTimeout(() => setSuccessMessage(""), 4000);
    } catch (error) {
      console.error("Error sending password reset email:", error);
      setSuccessMessage("Failed to send password reset email");
      setTimeout(() => setSuccessMessage(""), 4000);
    } finally {
      setSendingPasswordReset(null);
    }
  };

  const handleResetToDefault = async (u) => {
    setResettingToDefault(u.id);
    try {
      const response = await axios.post(`${API}/admin/users/${u.id}/reset-to-default`, {}, { headers });
      setSuccessMessage(`Password reset for ${u.name}. Default: ${response.data.default_password}`);
      setTimeout(() => setSuccessMessage(""), 6000);
    } catch (error) {
      console.error("Error resetting to default:", error);
      setSuccessMessage("Failed to reset password");
      setTimeout(() => setSuccessMessage(""), 4000);
    } finally {
      setResettingToDefault(null);
    }
  };

  const [fixingEmployeeLink, setFixingEmployeeLink] = useState(null);
  
  const handleFixEmployeeLink = async (u) => {
    setFixingEmployeeLink(u.id);
    try {
      // Try the direct fix by email endpoint first (more reliable)
      const response = await axios.post(`${API}/admin/fix-user-by-email?email=${encodeURIComponent(u.email)}`, {}, { headers });
      const msg = `✅ ${response.data.message}\n\nEmployee ID: ${response.data.employee_id}\n\n⚠️ ${u.name} must LOG OUT and LOG BACK IN!`;
      alert(msg);
      setSuccessMessage(`Fixed employee link for ${u.name}`);
      setTimeout(() => setSuccessMessage(""), 6000);
      fetchUsers();
    } catch (error) {
      console.error("Error fixing employee link:", error);
      const errMsg = error.response?.data?.detail || "Failed to fix employee link";
      alert(`❌ Error: ${errMsg}`);
    } finally {
      setFixingEmployeeLink(null);
    }
  };

  // Super Admin - Org visibility management functions
  const openOrgVisibilityDialog = async (u) => {
    setSelectedUserForOrgs(u);
    setIsOrgVisibilityDialogOpen(true);
    
    // Fetch all orgs (need to bypass the filter for this)
    try {
      // We'll get the orgs the super admin can see (all of them)
      const orgsResponse = await axios.get(`${API}/external/organizations`, { headers });
      setAllOrgsForVisibility(orgsResponse.data);
    } catch (error) {
      console.error("Error fetching orgs:", error);
    }
  };

  const toggleOrgForUser = async (orgSlug) => {
    if (!selectedUserForOrgs) return;
    setSavingOrgVisibility(true);
    
    try {
      const response = await axios.post(
        `${API}/admin/users/${selectedUserForOrgs.id}/toggle-org/${orgSlug}`,
        {},
        { headers }
      );
      
      // Update the selected user's allowed_orgs locally
      setSelectedUserForOrgs(prev => ({
        ...prev,
        allowed_orgs: response.data.allowed_orgs
      }));
      
      // Also update in the users list
      setUsers(prev => prev.map(u => 
        u.id === selectedUserForOrgs.id 
          ? { ...u, allowed_orgs: response.data.allowed_orgs }
          : u
      ));
      
      setSuccessMessage(`Org ${response.data.action === 'added' ? 'enabled' : 'disabled'} for ${selectedUserForOrgs.name}`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling org:", error);
      alert(error.response?.data?.detail || "Failed to toggle org access");
    } finally {
      setSavingOrgVisibility(false);
    }
  };



  const getDeptColor = (dept) => {
    return DEPARTMENTS[dept]?.color || "bg-slate-600";
  };

  const getDeptLabel = (dept) => {
    return DEPARTMENTS[dept]?.label || dept;
  };

  const openEditDialog = async (u) => {
    setEditingUser(u);
    setEditNotes(u.notes || "");
    setEditName(u.name || "");
    setEditDepartment(u.department || u.role || "sales");
    setEditJoinDate("");
    setIsEditDialogOpen(true);
    setLoadingDetails(true);
    
    try {
      const response = await axios.get(`${API}/admin/users/${u.id}/details`, { headers });
      setUserDetails(response.data);
      setEditNotes(response.data.user?.notes || "");
      setEditName(response.data.user?.name || "");
      setEditDepartment(response.data.user?.department || response.data.user?.role || "sales");
      // Format the date for input[type="date"]
      if (response.data.user?.created_at) {
        const date = new Date(response.data.user.created_at);
        setEditJoinDate(date.toISOString().split('T')[0]);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      setUserDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSaveUserDetails = async () => {
    if (!editingUser) return;
    
    setSavingUserEdit(true);
    try {
      const updateData = { 
        notes: editNotes,
        name: editName,
        department: editDepartment,
      };
      
      // Add join date if changed
      if (editJoinDate) {
        updateData.created_at = new Date(editJoinDate).toISOString();
      }
      
      await axios.put(`${API}/admin/users/${editingUser.id}`, updateData, { headers });
      setSuccessMessage("User details saved!");
      setTimeout(() => setSuccessMessage(""), 2000);
      fetchData();
      // Close the dialog after successful save
      setIsEditDialogOpen(false);
      setEditingUser(null);
      setUserDetails(null);
    } catch (error) {
      console.error("Error saving user details:", error);
    } finally {
      setSavingUserEdit(false);
    }
  };

  const getAwardIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-4 h-4 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-4 h-4 text-slate-300" />;
    if (rank === 3) return <Award className="w-4 h-4 text-amber-600" />;
    return null;
  };

  const getAwardLabel = (rank) => {
    if (rank === 1) return "1st Place";
    if (rank === 2) return "2nd Place";
    if (rank === 3) return "3rd Place";
    return `${rank}th Place`;
  };

  if (!["admin", "super_admin"].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <Shield className="w-16 h-16 mx-auto text-slate-500 mb-4" />
        <h2 className="text-xl font-semibold text-slate-300">Admin Access Required</h2>
        <p className="text-slate-500 mt-2">You don't have permission to view this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="admin-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Team Management
          </h1>
        </div>

        <Button
          size="sm"
          className="bg-indigo-600 hover:bg-indigo-700"
          onClick={() => {
            // Pre-select the right user type based on active tab
            setNewUser(prev => ({ 
              ...prev, 
              is_external: activeTab === "external",
              role: activeTab === "external" ? "user" : "sales"
            }));
            setIsAddDialogOpen(true);
          }}
          data-testid="add-user-btn"
        >
          <UserPlus className="mr-1 h-4 w-4" />
          Add
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab("internal")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === "internal"
              ? "bg-indigo-600 text-white"
              : "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
          }`}
        >
          <Users size={16} />
          Internal Team
          <span className={`px-1.5 py-0.5 rounded text-xs ${
            activeTab === "internal" ? "bg-indigo-500" : "bg-slate-700"
          }`}>
            {users.filter(u => !u.is_external).length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab("external")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === "external"
              ? "bg-cyan-600 text-white"
              : "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
          }`}
        >
          <Building2 size={16} />
          External Clients
          <span className={`px-1.5 py-0.5 rounded text-xs ${
            activeTab === "external" ? "bg-cyan-500" : "bg-slate-700"
          }`}>
            {users.filter(u => u.is_external).length}
          </span>
        </button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg flex items-center gap-2 text-emerald-400 text-sm">
          <CheckCircle size={16} />
          <span>{successMessage}</span>
        </div>
      )}

      {/* PII detection banner (shown after any CSV/Excel upload) */}
      {piiReport && (
        <PiiWarningBanner
          report={piiReport}
          onDismiss={() => setPiiReport(null)}
        />
      )}

      {/* Pending Rock Ideas Section */}
      {rockIdeas.filter(i => i.status === "pending").length > 0 && (
        <div className="mb-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <button
            onClick={() => setShowIdeasPanel(!showIdeasPanel)}
            className="w-full p-3 flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <Lightbulb size={16} className="text-amber-400" />
              <span className="text-sm font-medium text-amber-400">
                Pending Rock Ideas ({rockIdeas.filter(i => i.status === "pending").length})
              </span>
            </div>
            {showIdeasPanel ? <ChevronUp size={16} className="text-amber-400" /> : <ChevronDown size={16} className="text-amber-400" />}
          </button>
          
          {showIdeasPanel && (
            <div className="px-3 pb-3 space-y-2 max-h-[300px] overflow-y-auto">
              {rockIdeas.filter(i => i.status === "pending").map(idea => (
                <div key={idea.id} className="p-3 rounded-lg bg-slate-800/50 border border-amber-500/30">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${DEPARTMENTS[idea.department]?.color || 'bg-slate-600'} text-white`}>
                          {DEPARTMENTS[idea.department]?.label || idea.department}
                        </span>
                        <span className="text-xs text-slate-400">from {idea.user_name}</span>
                      </div>
                      <p className="text-sm text-slate-200">{idea.idea}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Submitted: {new Date(idea.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleUpdateIdeaStatus(idea.id, "approved")}
                        className="p-2 text-emerald-400 hover:bg-emerald-500/20 rounded-lg border border-emerald-500/30"
                        title="Approve"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={() => handleUpdateIdeaStatus(idea.id, "rejected")}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg border border-red-500/30"
                        title="Reject"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Internal Team Members Section */}
      {activeTab === "internal" && (
        <div className="mb-6">
          <div className="space-y-2" data-testid="internal-users-list">
            {[...users]
              .filter(u => !u.is_external)
              .sort((a, b) => {
                const rolePriority = { admin: 0, manager: 1 };
                const aRole = rolePriority[a.role] ?? 2;
                const bRole = rolePriority[b.role] ?? 2;
                if (aRole !== bRole) return aRole - bRole;
                return (a.name || '').localeCompare(b.name || '');
              }).map((u, index) => {
                const originalDept = u.department || u.role || "sales";
                const displayDept = pendingDeptChanges[u.id] || originalDept;
                const hasPendingChange = pendingDeptChanges[u.id] && pendingDeptChanges[u.id] !== originalDept;
                const deptConfig = DEPARTMENTS[displayDept] || DEPARTMENTS.sales;
                const isExpanded = expandedUserId === u.id;
                const borderColor = u.role === "admin" ? "border-purple-500/30" : 
                displayDept === "sales" ? "border-indigo-500/30" :
                displayDept === "csm" ? "border-emerald-500/30" :
                displayDept === "dev" ? "border-purple-500/30" :
                displayDept === "support" ? "border-amber-500/30" :
                displayDept === "marketing" ? "border-pink-500/30" : "border-slate-700/50";
              const bgColor = u.role === "admin" ? "bg-purple-500/5" :
                displayDept === "sales" ? "bg-indigo-500/5" :
                displayDept === "csm" ? "bg-emerald-500/5" :
                displayDept === "dev" ? "bg-purple-500/5" :
                displayDept === "support" ? "bg-amber-500/5" :
                displayDept === "marketing" ? "bg-pink-500/5" : "bg-slate-800/50";
          
          return (
            <div 
              key={u.id} 
              className={`${bgColor} border ${borderColor} ${hasPendingChange ? 'ring-2 ring-amber-500/50' : ''} rounded-lg overflow-hidden transition-all`}
              data-testid={`user-row-${index}`}
            >
              {/* Clickable Header Row */}
              <div 
                className="flex items-center gap-2 p-2 cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedUserId(isExpanded ? null : u.id)}
              >
                {/* Avatar with photo or initial */}
                <div 
                  className={`w-9 h-9 rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 overflow-hidden ${
                    u.role === "admin" ? "bg-purple-600" : deptConfig.color
                  }`}
                >
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt={u.name} className="w-full h-full object-cover" />
                  ) : (
                    u.name?.charAt(0)
                  )}
                </div>
                
                {/* Name & Email */}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-100 text-sm truncate flex items-center gap-2">
                    {u.name}
                    {u.is_external && (
                      <span className="px-1.5 py-0.5 bg-cyan-600/30 text-cyan-300 text-[9px] rounded">
                        EXTERNAL
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {u.email}
                    {u.is_external && u.account_name && (
                      <span className="ml-2 text-cyan-400">• {u.account_name}</span>
                    )}
                  </div>
                </div>
                
                {/* Department Badge */}
                <div className={`hidden sm:flex px-2 py-0.5 rounded text-[10px] font-medium text-white ${
                  u.role === "admin" ? "bg-purple-600" : deptConfig.color
                }`}>
                  {u.role === "admin" ? "Admin" : getDeptLabel(displayDept)}
                </div>
                
                {/* Status Indicator */}
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${u.is_active ? 'bg-emerald-500' : 'bg-slate-600'}`} 
                  title={u.is_active ? 'Active' : 'Inactive'} 
                />
                
                {/* Expand Indicator */}
                <div className="text-slate-500">
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>
              
              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-3 pb-3 pt-1 border-t border-slate-700/50 animate-fade-in">
                  {/* Quick Info Row */}
                  <div className="flex items-center gap-2 mb-3 text-xs text-slate-400">
                    <Mail size={12} />
                    <span className="truncate">{u.email}</span>
                  </div>
                  
                  {/* External User Info */}
                  {u.is_external && (
                    <div className="mb-3 p-2 bg-cyan-900/20 rounded-lg border border-cyan-700/30">
                      <div className="text-[10px] text-cyan-300 font-medium mb-2">Calldrip Client</div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-slate-500">Organization:</span>
                          <span className="text-slate-200 ml-1">{u.organization_name || u.calldrip_org_id}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Account:</span>
                          <span className="text-slate-200 ml-1">{u.account_name || u.calldrip_account_id}</span>
                        </div>
                        {u.calldrip_user_id && (
                          <div>
                            <span className="text-slate-500">User ID:</span>
                            <span className="text-slate-200 ml-1">{u.calldrip_user_id}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-slate-500">Access:</span>
                          <span className={`ml-1 ${
                            u.external_role === 'admin' ? 'text-purple-300' : 
                            u.external_role === 'manager' ? 'text-amber-300' : 'text-slate-300'
                          }`}>
                            {u.external_role === 'admin' ? 'Admin (all stores)' : 
                             u.external_role === 'manager' ? 'Manager' : 'User'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Department Selector (for non-admins, non-external) */}
                  {u.role !== "admin" && !u.is_external && (
                    <div className="mb-3">
                      <Label className="text-slate-400 text-xs mb-1 block">Department</Label>
                      <div className="flex items-center gap-2">
                        <Select 
                          value={displayDept} 
                          onValueChange={(v) => handleDepartmentChange(u.id, v)}
                        >
                          <SelectTrigger className="h-8 flex-1 bg-slate-900/50 border-slate-600 text-white text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            {Object.entries(DEPARTMENTS).map(([key, val]) => (
                              <SelectItem key={key} value={key} className="text-slate-200 text-sm">
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${val.color}`}></div>
                                  {val.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {hasPendingChange && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleSaveDepartment(u.id)}
                              disabled={savingUserId === u.id}
                              className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700"
                            >
                              {savingUserId === u.id ? <div className="loading-spinner w-3 h-3"></div> : "Save"}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCancelDeptChange(u.id)}
                              className="h-8 w-8 p-0"
                            >
                              <X size={14} />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Role Selector */}
                  <div className="mb-3">
                    <Label className="text-slate-400 text-xs mb-1 block">Role</Label>
                    <div className="flex items-center gap-1 bg-slate-900/50 rounded-lg p-1">
                      {Object.entries(ROLES).map(([roleKey, roleConfig]) => {
                        const RoleIcon = roleConfig.icon;
                        const isCurrentRole = (u.role === roleKey) || (u.role === "admin" && roleKey === "admin") || (!["admin", "manager"].includes(u.role) && roleKey === "user");
                        const isSelf = user?.id && u.id === user.id;
                        return (
                          <button
                            key={roleKey}
                            onClick={() => !isSelf && handleRoleChange(u.id, roleKey)}
                            disabled={isSelf}
                            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-md transition-all text-xs border ${
                              isCurrentRole 
                                ? `${roleConfig.bg} ${roleConfig.color} ${roleConfig.border} shadow-sm` 
                                : `${roleConfig.inactiveText} border-slate-600 ${roleConfig.hoverBg} ${roleConfig.hoverBorder} hover:${roleConfig.inactiveText.replace('/60', '')}`
                            } ${isSelf ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                            title={isSelf ? "Cannot change your own role" : roleConfig.label}
                          >
                            <RoleIcon size={16} />
                            <span className="hidden sm:inline">{roleConfig.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Status Toggle */}
                  <div className="flex items-center justify-between mb-3 p-2 bg-slate-900/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-300 text-sm">Account Active</span>
                    </div>
                    <Switch
                      checked={u.is_active}
                      onCheckedChange={() => handleToggleActive(u.id, u.is_active)}
                      disabled={user?.id && u.id === user.id}
                    />
                  </div>
                  
                  {/* Last Login Info */}
                  <div className="mb-3 p-2 bg-slate-900/30 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-xs">Last Login</span>
                      <span className="text-slate-300 text-sm">
                        {u.last_login 
                          ? new Date(u.last_login).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })
                          : <span className="text-slate-500 italic">Never</span>
                        }
                      </span>
                    </div>
                  </div>
                  
                  {/* Action Buttons with Labels */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); openEditDialog(u); }}
                      className="h-9 text-xs border-slate-600 hover:bg-slate-700 hover:border-indigo-500"
                    >
                      <Pencil size={14} className="mr-1.5" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); setUploadingUserId(u.id); avatarInputRef.current?.click(); }}
                      className="h-9 text-xs border-slate-600 hover:bg-slate-700 hover:border-emerald-500"
                    >
                      <Camera size={14} className="mr-1.5" />
                      Photo
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); handleImpersonate(u); }}
                      disabled={u.id === user?.id}
                      className="h-9 text-xs border-slate-600 hover:bg-slate-700 hover:border-indigo-500 disabled:opacity-30"
                    >
                      <UserCheck size={14} className="mr-1.5" />
                      View As
                    </Button>
                    {/* Org Access button - only for super_admin managing admin users */}
                    {user?.role === "super_admin" && u.role === "admin" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); openOrgVisibilityDialog(u); }}
                        className="h-9 text-xs border-slate-600 hover:bg-slate-700 hover:border-emerald-500 hover:text-emerald-400"
                      >
                        <Shield size={14} className="mr-1.5" />
                        Org Access
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); handleResetToDefault(u); }}
                      disabled={resettingToDefault === u.id}
                      className="h-9 text-xs border-slate-600 hover:bg-slate-700 hover:border-amber-500"
                    >
                      {resettingToDefault === u.id ? (
                        <div className="loading-spinner w-3 h-3 mr-1.5"></div>
                      ) : (
                        <KeyRound size={14} className="mr-1.5" />
                      )}
                      {resettingToDefault === u.id ? "Resetting..." : "Reset Password"}
                    </Button>
                    {u.role !== "admin" && !u.is_external && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); handleFixEmployeeLink(u); }}
                        disabled={fixingEmployeeLink === u.id}
                        className="h-9 text-xs border-slate-600 hover:bg-slate-700 hover:border-green-500"
                      >
                        {fixingEmployeeLink === u.id ? (
                          <div className="loading-spinner w-3 h-3 mr-1.5"></div>
                        ) : (
                          <Link size={14} className="mr-1.5" />
                        )}
                        {fixingEmployeeLink === u.id ? "Fixing..." : "Fix Employee"}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); handleDeleteUser(u.id); }}
                      disabled={u.id === user?.id}
                      className="h-9 text-xs border-slate-600 hover:bg-red-500/20 hover:border-red-500 hover:text-red-400 disabled:opacity-30"
                    >
                      <Trash2 size={14} className="mr-1.5" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>
      )}

      {/* External Client Users Section */}
      {activeTab === "external" && (
        <div className="mb-6">
          {/* Upload Buttons Row */}
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Upload Report Card */}
            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 flex items-center justify-between">
              <div>
                <p className="text-slate-300 text-sm font-medium">Upload Dashboard</p>
                <p className="text-slate-500 text-xs">CSV or HTML report</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsUploadReportDialogOpen(true)}
                className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
              >
                <Upload className="mr-1 h-4 w-4" />
                Upload
              </Button>
            </div>
            
            {/* Manage Reports Card */}
            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 flex items-center justify-between">
              <div>
                <p className="text-slate-300 text-sm font-medium">Manage Reports</p>
                <p className="text-slate-500 text-xs">Edit or delete</p>
              </div>
              <select
                onChange={(e) => {
                  const account = accounts.find(a => a.calldrip_account_id === e.target.value);
                  if (account) loadAccountReports(account);
                  e.target.value = "";
                }}
                className="bg-slate-700 border border-amber-500/50 text-amber-400 text-xs rounded px-2 py-1.5 cursor-pointer hover:bg-slate-600"
                defaultValue=""
              >
                <option value="" disabled>Select</option>
                {accounts.map(acc => (
                  <option key={acc.calldrip_account_id} value={acc.calldrip_account_id}>
                    {acc.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Upload Logo Card */}
            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 flex items-center justify-between">
              <div>
                <p className="text-slate-300 text-sm font-medium">Client Branding</p>
                <p className="text-slate-500 text-xs">Upload logos</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsUploadLogoDialogOpen(true)}
                className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
              >
                <Camera className="mr-1 h-4 w-4" />
                Logo
              </Button>
            </div>
          </div>
          
          {/* Client Reports Section - View reports by account */}
          {accounts.length > 0 && (
            <div className="mb-6">
              <h3 className="text-slate-300 text-sm font-medium mb-3 flex items-center gap-2">
                <Eye size={16} className="text-cyan-400" />
                Client Reports
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {accounts.map((acc) => {
                  const org = organizations.find(o => o.calldrip_org_id === acc.calldrip_org_id);
                  return (
                    <div 
                      key={acc.calldrip_account_id}
                      className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-cyan-500/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-slate-200 text-sm font-medium truncate">{acc.name}</p>
                          <p className="text-slate-500 text-xs truncate">{org?.name || 'Unknown Org'}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`/reports/${org?.slug || 'unknown'}/${acc.slug}`, '_blank')}
                          className="h-7 px-2 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20 flex-shrink-0"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {users.filter(u => u.is_external).length === 0 ? (
            <div className="text-center py-12 bg-slate-800/50 rounded-lg border border-slate-700">
              <Building2 className="w-12 h-12 mx-auto text-slate-500 mb-3" />
              <p className="text-slate-400 mb-2">No external clients yet</p>
              <p className="text-slate-500 text-sm mb-4">Add your first client user to get started</p>
              <Button
                size="sm"
                className="bg-cyan-600 hover:bg-cyan-700"
                onClick={() => {
                  setNewUser(prev => ({ ...prev, is_external: true, role: "user" }));
                  setIsAddDialogOpen(true);
                }}
              >
                <UserPlus className="mr-1 h-4 w-4" />
                Add External Client
              </Button>
            </div>
          ) : (
            <div className="space-y-2" data-testid="external-users-list">
              {[...users]
                .filter(u => u.is_external)
                .sort((a, b) => {
                  // Sort by organization, then account, then name
                  const orgCompare = (a.organization_name || '').localeCompare(b.organization_name || '');
                  if (orgCompare !== 0) return orgCompare;
                  const accCompare = (a.account_name || '').localeCompare(b.account_name || '');
                  if (accCompare !== 0) return accCompare;
                  return (a.name || '').localeCompare(b.name || '');
                }).map((u, index) => {
                  const isExpanded = expandedUserId === u.id;
                  
                  return (
                    <div 
                      key={u.id} 
                      className="bg-cyan-500/5 border border-cyan-500/30 rounded-lg overflow-hidden transition-all"
                      data-testid={`external-user-row-${index}`}
                    >
                      {/* Clickable Header Row */}
                      <div 
                        className="flex items-center gap-2 p-2 cursor-pointer hover:bg-white/5 transition-colors"
                        onClick={() => setExpandedUserId(isExpanded ? null : u.id)}
                      >
                        {/* Avatar with photo or initial */}
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 overflow-hidden bg-cyan-600">
                          {u.avatar_url ? (
                            <img src={u.avatar_url} alt={u.name} className="w-full h-full object-cover" />
                          ) : (
                            u.name?.charAt(0)
                          )}
                        </div>
                      
                      {/* Name & Organization/Account */}
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-100 text-sm truncate">
                          {u.name}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {u.organization_name && <span className="text-cyan-400">{u.organization_name}</span>}
                          {u.account_name && <span className="text-slate-500"> → {u.account_name}</span>}
                        </div>
                      </div>
                      
                      {/* External Role Badge */}
                      <div className="hidden sm:flex px-2 py-0.5 rounded text-[10px] font-medium text-white bg-cyan-600">
                        {u.external_role === 'admin' ? 'Client Admin' : 
                         u.external_role === 'manager' ? 'Manager' : 'User'}
                      </div>
                      
                      {/* Status Indicator */}
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${u.is_active ? 'bg-emerald-500' : 'bg-slate-600'}`} 
                        title={u.is_active ? 'Active' : 'Inactive'} 
                      />
                      
                      {/* Expand Indicator */}
                      <div className="text-slate-500">
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>
                    
                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="p-3 border-t border-cyan-500/20 bg-slate-900/30">
                        {/* Client Info */}
                        <div className="mb-3 p-2 bg-slate-900/30 rounded-lg space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-400 text-xs">Email</span>
                            <span className="text-slate-300 text-sm">{u.email}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-400 text-xs">Organization</span>
                            <span className="text-cyan-300 text-sm">{u.organization_name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-400 text-xs">Account</span>
                            <span className="text-cyan-300 text-sm">{u.account_name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-400 text-xs">Access Level</span>
                            <span className="text-slate-300 text-sm capitalize">{u.external_role || 'user'}</span>
                          </div>
                          {u.calldrip_user_id && (
                            <div className="flex items-center justify-between">
                              <span className="text-slate-400 text-xs">Calldrip User ID</span>
                              <span className="text-slate-300 text-sm font-mono">{u.calldrip_user_id}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Active Status Toggle */}
                        <div className="flex items-center justify-between mb-3 p-2 bg-slate-900/30 rounded-lg">
                          <div>
                            <span className="text-slate-300 text-sm">Account Active</span>
                            <p className="text-[10px] text-slate-500">User can log in when active</p>
                          </div>
                          <Switch
                            checked={u.is_active}
                            onCheckedChange={() => handleToggleActive(u.id, u.is_active)}
                          />
                        </div>
                        
                        {/* Last Login Info */}
                        <div className="mb-3 p-2 bg-slate-900/30 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-400 text-xs">Last Login</span>
                            <span className="text-slate-300 text-sm">
                              {u.last_login 
                                ? new Date(u.last_login).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })
                                : <span className="text-slate-500 italic">Never</span>
                              }
                            </span>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); openEditDialog(u); }}
                            className="h-9 text-xs border-slate-600 hover:bg-slate-700 hover:border-cyan-500"
                          >
                            <Pencil size={14} className="mr-1.5" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleImpersonate(u); }}
                            className="h-9 text-xs border-slate-600 hover:bg-slate-700 hover:border-cyan-500"
                          >
                            <UserCheck size={14} className="mr-1.5" />
                            View As
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleResetToDefault(u); }}
                            disabled={resettingToDefault === u.id}
                            className="h-9 text-xs border-slate-600 hover:bg-slate-700 hover:border-amber-500"
                          >
                            {resettingToDefault === u.id ? (
                              <div className="loading-spinner w-3 h-3 mr-1.5"></div>
                            ) : (
                              <KeyRound size={14} className="mr-1.5" />
                            )}
                            Reset Password
                          </Button>
                          {u.role !== "admin" && !u.is_external && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); handleFixEmployeeLink(u); }}
                              disabled={fixingEmployeeLink === u.id}
                              className="h-9 text-xs border-slate-600 hover:bg-slate-700 hover:border-green-500"
                            >
                              {fixingEmployeeLink === u.id ? (
                                <div className="loading-spinner w-3 h-3 mr-1.5"></div>
                              ) : (
                                <Link size={14} className="mr-1.5" />
                              )}
                              Fix Employee
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleDeleteUser(u.id); }}
                            className="h-9 text-xs border-slate-600 hover:bg-red-500/20 hover:border-red-500 hover:text-red-400"
                          >
                            <Trash2 size={14} className="mr-1.5" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Hidden file input for avatar upload */}
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/*"
        onChange={handleAvatarUpload}
        className="hidden"
      />

      {/* Add User Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent data-testid="add-user-dialog" className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Add User</DialogTitle>
            <DialogDescription className="text-slate-400">
              Create an internal team member or external client user.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            {/* User Type Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700">
              <div>
                <Label className="text-slate-300 text-sm">External Client User</Label>
                <p className="text-xs text-slate-500">Toggle on for Calldrip client users</p>
              </div>
              <Switch
                checked={newUser.is_external}
                onCheckedChange={(checked) => setNewUser({ 
                  ...newUser, 
                  is_external: checked,
                  role: checked ? "user" : "sales"
                })}
              />
            </div>
            
            <div className="grid gap-1.5">
              <Label className="text-slate-300 text-sm">Full Name</Label>
              <Input
                placeholder="Enter name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white h-9"
                data-testid="new-user-name"
              />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-slate-300 text-sm">Email</Label>
              <Input
                type="email"
                placeholder={newUser.is_external ? "user@company.com" : "name@calldrip.com"}
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white h-9"
                data-testid="new-user-email"
              />
            </div>
            
            {/* External User Fields */}
            {newUser.is_external && (
              <div className="space-y-3 p-3 bg-indigo-900/20 rounded-lg border border-indigo-700/50">
                <p className="text-xs text-indigo-300 font-medium">Calldrip Client Information</p>
                
                <div className="grid gap-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300 text-sm">Organization</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsNewOrgDialogOpen(true)}
                      className="h-6 px-2 text-indigo-400 hover:text-indigo-300 hover:bg-indigo-900/30"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      New
                    </Button>
                  </div>
                  <Select
                    value={newUser.calldrip_org_id}
                    onValueChange={(v) => {
                      setNewUser({ ...newUser, calldrip_org_id: v, calldrip_account_id: "" });
                    }}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white h-9">
                      <SelectValue placeholder="Select organization..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {organizations.map((org) => (
                        <SelectItem key={org.calldrip_org_id} value={org.calldrip_org_id} className="text-slate-200">
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Or enter Org ID manually"
                    value={newUser.calldrip_org_id}
                    onChange={(e) => setNewUser({ ...newUser, calldrip_org_id: e.target.value })}
                    className="bg-slate-900 border-slate-600 text-white h-8 text-xs"
                  />
                </div>
                
                <div className="grid gap-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300 text-sm">Account</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (!newUser.calldrip_org_id) {
                          alert("Please select an organization first");
                          return;
                        }
                        setIsNewAccountDialogOpen(true);
                      }}
                      className="h-6 px-2 text-indigo-400 hover:text-indigo-300 hover:bg-indigo-900/30"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      New
                    </Button>
                  </div>
                  <Select
                    value={newUser.calldrip_account_id}
                    onValueChange={(v) => setNewUser({ ...newUser, calldrip_account_id: v })}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white h-9">
                      <SelectValue placeholder="Select account..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {accounts
                        .filter(a => !newUser.calldrip_org_id || a.calldrip_org_id === newUser.calldrip_org_id)
                        .map((acc) => (
                          <SelectItem key={acc.calldrip_account_id} value={acc.calldrip_account_id} className="text-slate-200">
                            {acc.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Or enter Account ID manually"
                    value={newUser.calldrip_account_id}
                    onChange={(e) => setNewUser({ ...newUser, calldrip_account_id: e.target.value })}
                    className="bg-slate-900 border-slate-600 text-white h-8 text-xs"
                  />
                </div>
                
                <div className="grid gap-1.5">
                  <Label className="text-slate-300 text-sm">Calldrip User ID</Label>
                  <Input
                    placeholder="User ID from Calldrip (optional)"
                    value={newUser.calldrip_user_id}
                    onChange={(e) => setNewUser({ ...newUser, calldrip_user_id: e.target.value })}
                    className="bg-slate-900 border-slate-600 text-white h-9"
                  />
                </div>
                
                <div className="grid gap-1.5">
                  <Label className="text-slate-300 text-sm">Client Access Level</Label>
                  <Select
                    value={newUser.external_role}
                    onValueChange={(v) => setNewUser({ ...newUser, external_role: v })}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="admin" className="text-slate-200">Admin (sees all stores)</SelectItem>
                      <SelectItem value="manager" className="text-slate-200">Manager (assigned stores)</SelectItem>
                      <SelectItem value="user" className="text-slate-200">User (limited view)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            {/* Internal User Fields */}
            {!newUser.is_external && (
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label className="text-slate-300 text-sm">Role</Label>
                  <Select
                    value={newUser.role}
                    onValueChange={(v) => setNewUser({ ...newUser, role: v, department: v === "admin" ? "sales" : v })}
                  >
                    <SelectTrigger data-testid="new-user-role" className="bg-slate-900 border-slate-600 text-white h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {ROLES_DROPDOWN.map((r) => (
                        <SelectItem key={r.value} value={r.value} className="text-slate-200">
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label className="text-slate-300 text-sm">Department</Label>
                  <Select
                    value={newUser.department}
                    onValueChange={(v) => setNewUser({ ...newUser, department: v })}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {Object.entries(DEPARTMENTS).map(([key, val]) => (
                        <SelectItem key={key} value={key} className="text-slate-200">
                          {val.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            <div className="p-2 bg-emerald-900/20 rounded-lg border border-emerald-700/30">
              <p className="text-xs text-emerald-300">
                Default password: <span className="font-mono font-bold">Calldrip1</span>
              </p>
              <p className="text-xs text-slate-400 mt-1">User will be prompted to change on first login. Welcome email will be sent automatically.</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleAddUser}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-user-btn"
            >
              Add User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent data-testid="edit-user-dialog" className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Pencil size={16} />
              Edit Team Member
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              View and edit details for <strong className="text-slate-200">{editingUser?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="py-8 flex justify-center">
              <div className="loading-spinner"></div>
            </div>
          ) : userDetails ? (
            <div className="py-3 space-y-4">
              {/* User Info Section */}
              <div className="bg-slate-900/50 rounded-lg p-3">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg ${
                    DEPARTMENTS[editingUser?.department]?.color || "bg-slate-600"
                  }`}>
                    {editName?.charAt(0) || editingUser?.name?.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">{editingUser?.email}</p>
                  </div>
                </div>
                
                {/* Editable Name */}
                <div className="grid gap-2 mb-3">
                  <Label className="text-slate-400 text-xs">Name</Label>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="h-8 bg-slate-800 border-slate-600 text-white text-sm"
                    placeholder="Employee name"
                  />
                </div>
                
                {/* Editable Join Date */}
                <div className="grid gap-2 mb-3">
                  <Label className="text-slate-400 text-xs flex items-center gap-1">
                    <Calendar size={12} />
                    Joined Team
                  </Label>
                  <Input
                    type="date"
                    value={editJoinDate}
                    onChange={(e) => setEditJoinDate(e.target.value)}
                    className="h-8 bg-slate-800 border-slate-600 text-white text-sm"
                  />
                </div>
                
                {/* Editable Department */}
                <div className="grid gap-2 mb-3">
                  <Label className="text-slate-400 text-xs flex items-center gap-1">
                    <Building2 size={12} />
                    Department / Team
                  </Label>
                  <Select
                    value={editDepartment}
                    onValueChange={(v) => setEditDepartment(v)}
                  >
                    <SelectTrigger className="h-8 bg-slate-800 border-slate-600 text-white text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {Object.entries(DEPARTMENTS).map(([key, val]) => (
                        <SelectItem key={key} value={key} className="text-slate-200 text-sm">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${val.color}`}></div>
                            {val.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Tenure Display */}
                <div className="flex items-center gap-2 text-sm p-2 bg-slate-800/50 rounded">
                  <Clock size={14} className="text-slate-500" />
                  <span className="text-slate-400">Tenure:</span>
                  <span className="text-emerald-400 font-medium">{userDetails.tenure_text}</span>
                  <span className="text-slate-600">({userDetails.tenure_days} days)</span>
                </div>
              </div>
              
              {/* Account Logo Section - Only for External Users */}
              {editingUser?.is_external && editingUser?.calldrip_account_id && (
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <Label className="text-slate-300 text-sm flex items-center gap-1 mb-3">
                    <Camera size={14} />
                    Account Logo
                  </Label>
                  <div className="flex items-center gap-4">
                    {/* Current Logo Preview */}
                    <div className="w-20 h-20 bg-slate-800 rounded-lg flex items-center justify-center overflow-hidden border border-slate-700">
                      {(() => {
                        const account = accounts.find(a => a.calldrip_account_id === editingUser.calldrip_account_id);
                        if (account?.logo_url) {
                          return (
                            <img 
                              src={`${API.replace('/api', '')}${account.logo_url}`}
                              alt="Account logo"
                              className="w-full h-full object-contain"
                            />
                          );
                        }
                        return (
                          <div className="text-slate-600 text-xs text-center p-2">
                            No logo
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* Upload Button */}
                    <div className="flex-1">
                      <p className="text-xs text-slate-500 mb-2">
                        {accounts.find(a => a.calldrip_account_id === editingUser.calldrip_account_id)?.name || 'Account'} logo
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => editLogoInputRef.current?.click()}
                        disabled={uploadingEditLogo}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      >
                        <Upload size={14} className="mr-2" />
                        {uploadingEditLogo ? "Uploading..." : "Change Logo"}
                      </Button>
                      <p className="text-[10px] text-slate-600 mt-1">
                        PNG, JPG recommended. Max 5MB.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Notes Section */}
              <div>
                <Label className="text-slate-300 text-sm flex items-center gap-1 mb-2">
                  <FileText size={14} />
                  Admin Notes
                </Label>
                <Textarea
                  placeholder="Add notes about this employee..."
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  className="bg-slate-900 border-slate-600 text-white min-h-[60px] text-sm"
                />
              </div>
              
              {/* Awards Section */}
              <div>
                <Label className="text-slate-300 text-sm flex items-center gap-1 mb-2">
                  <Trophy size={14} />
                  Awards History
                </Label>
                {userDetails.awards && userDetails.awards.length > 0 ? (
                  <div className="space-y-2 max-h-[150px] overflow-y-auto">
                    {userDetails.awards.map((award, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-slate-900/50 rounded-lg">
                        {getAwardIcon(award.rank)}
                        <div className="flex-1">
                          <div className="text-sm text-slate-200">{getAwardLabel(award.rank)} - {award.award_type.replace('_', ' ')}</div>
                          <div className="text-xs text-slate-500">
                            {new Date(2000, award.month - 1).toLocaleString('default', { month: 'long' })} {award.year}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-emerald-400">{award.value.toFixed(0)}</div>
                          <div className="text-[10px] text-slate-500">{award.metric}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-3 text-slate-500 text-sm bg-slate-900/30 rounded-lg">
                    <Trophy size={20} className="mx-auto mb-1 opacity-30" />
                    <p>No awards yet</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-slate-500">
              Failed to load user details
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSaveUserDetails}
              disabled={savingUserEdit}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingUserEdit ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      {/* New Organization Dialog */}
      <Dialog open={isNewOrgDialogOpen} onOpenChange={setIsNewOrgDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus size={18} className="text-indigo-400" />
              New Organization
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new client organization (e.g., Kubota, John Deere)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label className="text-slate-300 text-sm">Organization Name *</Label>
              <Input
                placeholder="e.g., Kubota"
                value={newOrg.name}
                onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-slate-400 text-xs">Calldrip Org ID (auto-generated if empty)</Label>
              <Input
                placeholder="ORG-KUBOTA-001"
                value={newOrg.calldrip_org_id}
                onChange={(e) => setNewOrg({ ...newOrg, calldrip_org_id: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white text-sm h-8"
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-slate-400 text-xs">URL Slug (auto-generated if empty)</Label>
              <Input
                placeholder="kubota"
                value={newOrg.slug}
                onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white text-sm h-8"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsNewOrgDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateOrganization}
              disabled={savingOrg || !newOrg.name}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {savingOrg ? "Creating..." : "Create Organization"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Account Dialog */}
      <Dialog open={isNewAccountDialogOpen} onOpenChange={setIsNewAccountDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus size={18} className="text-indigo-400" />
              New Account
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new client account under the selected organization
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label className="text-slate-400 text-xs">Organization</Label>
              <div className="px-3 py-2 bg-slate-900 border border-slate-600 rounded-md text-white text-sm">
                {organizations.find(o => o.calldrip_org_id === newUser.calldrip_org_id)?.name || newUser.calldrip_org_id}
              </div>
            </div>
            <div className="grid gap-2">
              <Label className="text-slate-300 text-sm">Account Name *</Label>
              <Input
                placeholder="e.g., Florida Coast Equipment"
                value={newAccount.name}
                onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-slate-400 text-xs">Calldrip Account ID (auto-generated if empty)</Label>
              <Input
                placeholder="ACC-FLORIDACOAST-001"
                value={newAccount.calldrip_account_id}
                onChange={(e) => setNewAccount({ ...newAccount, calldrip_account_id: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white text-sm h-8"
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-slate-400 text-xs">URL Slug (auto-generated if empty)</Label>
              <Input
                placeholder="floridacoast"
                value={newAccount.slug}
                onChange={(e) => setNewAccount({ ...newAccount, slug: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white text-sm h-8"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsNewAccountDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAccount}
              disabled={savingAccount || !newAccount.name}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {savingAccount ? "Creating..." : "Create Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Report Dialog */}
      <Dialog open={isUploadReportDialogOpen} onOpenChange={setIsUploadReportDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white flex items-center gap-2">
              <Upload size={18} className="text-cyan-400" />
              Upload Client Report
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Upload an HTML dashboard report for a client account
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
            <div className="grid gap-2">
              <Label className="text-slate-300 text-sm">Organization *</Label>
              <Select
                value={uploadReportOrg}
                onValueChange={(v) => {
                  setUploadReportOrg(v);
                  setUploadReportAccount("");
                }}
              >
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                  <SelectValue placeholder="Select organization..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {organizations.map((org) => (
                    <SelectItem key={org.calldrip_org_id} value={org.calldrip_org_id} className="text-slate-200">
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-2">
              <Label className="text-slate-300 text-sm">Account *</Label>
              <Select
                value={uploadReportAccount}
                onValueChange={setUploadReportAccount}
                disabled={!uploadReportOrg}
              >
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                  <SelectValue placeholder={uploadReportOrg ? "Select account..." : "Select organization first"} />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {accounts
                    .filter(a => a.calldrip_org_id === uploadReportOrg)
                    .map((acc) => (
                      <SelectItem key={acc.calldrip_account_id} value={acc.calldrip_account_id} className="text-slate-200">
                        {acc.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-2">
              <Label className="text-slate-300 text-sm">Report Title</Label>
              <Input
                placeholder="e.g., Monthly Performance Dashboard"
                value={uploadReportTitle}
                onChange={(e) => setUploadReportTitle(e.target.value)}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            
            <div className="grid gap-2">
              <Label className="text-slate-300 text-sm">Report Period</Label>
              <Input
                placeholder="e.g., January 2026"
                value={uploadReportPeriod}
                onChange={(e) => setUploadReportPeriod(e.target.value)}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            
            <div className="grid gap-2">
              <Label className="text-slate-300 text-sm">Report Type</Label>
              <Select
                value={uploadReportType}
                onValueChange={setUploadReportType}
              >
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                  <SelectValue placeholder="Auto-detect" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="auto" className="text-slate-200">
                    Auto-detect
                  </SelectItem>
                  <SelectItem value="kubota" className="text-slate-200">
                    Kubota Multi-Location
                  </SelectItem>
                  <SelectItem value="standalone_inbound" className="text-slate-200">
                    Standalone Inbound
                  </SelectItem>
                  <SelectItem value="standalone_outbound" className="text-slate-200">
                    Standalone Outbound
                  </SelectItem>
                  <SelectItem value="calldrip_scorecard" className="text-slate-200">
                    📊 Calldrip Scorecard (3 files)
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-slate-500 text-xs">Leave on auto-detect unless you know the specific format</p>
            </div>
            
            {/* Additional file inputs for Calldrip Scorecard */}
            {uploadReportType === "calldrip_scorecard" && (
              <div className="grid gap-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <p className="text-purple-300 text-xs font-medium">Calldrip Scorecard - Upload 1-3 CSV files:</p>
                
                {/* Theme Selector */}
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-purple-400 text-xs">Theme:</span>
                  <div className="flex rounded-lg overflow-hidden border border-purple-500/50">
                    <button
                      type="button"
                      onClick={() => setUploadReportTheme('dark')}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        uploadReportTheme === 'dark' 
                          ? 'bg-purple-600 text-white' 
                          : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      🌙 Dark
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadReportTheme('light')}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        uploadReportTheme === 'light' 
                          ? 'bg-purple-600 text-white' 
                          : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      ☀️ Light
                    </button>
                  </div>
                </div>
                
                <div className="grid gap-3">
                  {/* Activities CSV - Required */}
                  <div className="flex items-center gap-2 p-2 bg-orange-500/10 border border-orange-500/30 rounded-md">
                    <span className="text-orange-400 text-xs font-semibold w-40">1. Activities CSV *</span>
                    <input
                      type="file"
                      accept=".csv"
                      ref={activitiesFileRef}
                      onChange={(e) => setActivitiesFile(e.target.files?.[0] || null)}
                      className="flex-1 text-xs text-slate-400 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:bg-orange-600 file:text-white hover:file:bg-orange-500 file:cursor-pointer"
                    />
                    {activitiesFile && <span className="text-green-400 text-xs">✓ {activitiesFile.name}</span>}
                  </div>
                  
                  {/* Scored Metrics CSV - Optional */}
                  <div className="flex items-center gap-2 p-2 bg-slate-700/30 rounded-md">
                    <span className="text-purple-400 text-xs w-40">2. Scored Metrics CSV</span>
                    <input
                      type="file"
                      accept=".csv"
                      ref={scoredMetricsFileRef}
                      onChange={(e) => setScoredMetricsFile(e.target.files?.[0] || null)}
                      className="flex-1 text-xs text-slate-400 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:bg-purple-700 file:text-white hover:file:bg-purple-600 file:cursor-pointer"
                    />
                    {scoredMetricsFile && <span className="text-green-400 text-xs">✓ {scoredMetricsFile.name}</span>}
                  </div>
                  
                  {/* Customer Happiness CSV - Optional */}
                  <div className="flex items-center gap-2 p-2 bg-slate-700/30 rounded-md">
                    <span className="text-purple-400 text-xs w-40">3. Customer Happiness CSV</span>
                    <input
                      type="file"
                      accept=".csv"
                      ref={customerHappinessFileRef}
                      onChange={(e) => setCustomerHappinessFile(e.target.files?.[0] || null)}
                      className="flex-1 text-xs text-slate-400 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:bg-purple-700 file:text-white hover:file:bg-purple-600 file:cursor-pointer"
                    />
                    {customerHappinessFile && <span className="text-green-400 text-xs">✓ {customerHappinessFile.name}</span>}
                  </div>
                </div>
                
                {/* Files summary */}
                <div className="text-xs text-slate-500 mt-1">
                  {activitiesFile || scoredMetricsFile || customerHappinessFile ? (
                    <span className="text-green-400">
                      {[activitiesFile, scoredMetricsFile, customerHappinessFile].filter(Boolean).length} file(s) selected
                    </span>
                  ) : (
                    <span>Select Activities CSV (required) and optionally add Scored Metrics and/or Customer Happiness</span>
                  )}
                </div>
              </div>
            )}
            
            {uploadReportOrg && uploadReportAccount && (
              <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <p className="text-cyan-300 text-xs">
                  Report will be viewable at:
                </p>
                <p className="text-cyan-400 text-sm font-mono mt-1">
                  /reports/{organizations.find(o => o.calldrip_org_id === uploadReportOrg)?.slug}/{accounts.find(a => a.calldrip_account_id === uploadReportAccount)?.slug}
                </p>
              </div>
            )}
            
            <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600">
              <p className="text-slate-300 text-xs font-medium mb-1">Supported file types:</p>
              <p className="text-slate-400 text-xs">
                <span className="text-green-400">CSV, Excel</span> - Data will be analyzed and a report auto-generated<br/>
                <span className="text-blue-400">HTML</span> - Will be uploaded as-is
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 flex-shrink-0 pt-4 border-t border-slate-700">
            <Button variant="outline" onClick={() => setIsUploadReportDialogOpen(false)}>
              Cancel
            </Button>
            {uploadReportType === "calldrip_scorecard" ? (
              <Button 
                onClick={async () => {
                  if (!activitiesFile) {
                    alert("Please select an Activities CSV file (required)");
                    return;
                  }
                  const org = organizations.find(o => o.calldrip_org_id === uploadReportOrg);
                  const account = accounts.find(a => a.calldrip_account_id === uploadReportAccount);
                  if (org && account) {
                    await handleScorecardUpload(activitiesFile, org, account);
                  }
                }}
                disabled={uploadingReport || !uploadReportOrg || !uploadReportAccount || !activitiesFile}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {uploadingReport ? "Generating..." : `Generate Scorecard (${[activitiesFile, scoredMetricsFile, customerHappinessFile].filter(Boolean).length} file${[activitiesFile, scoredMetricsFile, customerHappinessFile].filter(Boolean).length !== 1 ? 's' : ''})`}
              </Button>
            ) : (
              <Button 
                onClick={() => reportFileInputRef.current?.click()}
                disabled={uploadingReport || !uploadReportOrg || !uploadReportAccount}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                {uploadingReport ? "Processing..." : "Select File"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Hidden file input for report upload */}
      <input
        ref={reportFileInputRef}
        type="file"
        accept=".html,.htm,.csv,.xlsx,.xls"
        onChange={handleUploadReport}
        className="hidden"
      />

      {/* Upload Logo Dialog */}
      <Dialog open={isUploadLogoDialogOpen} onOpenChange={setIsUploadLogoDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Camera size={18} className="text-emerald-400" />
              Upload Client Logo
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Upload a logo for a client account. This will appear in their portal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label className="text-slate-300 text-sm">Account *</Label>
              <Select
                value={uploadLogoAccount}
                onValueChange={setUploadLogoAccount}
              >
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                  <SelectValue placeholder="Select account..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {accounts.map((acc) => (
                    <SelectItem key={acc.calldrip_account_id} value={acc.calldrip_account_id} className="text-slate-200">
                      <div className="flex items-center gap-2">
                        {acc.logo_url && (
                          <img src={`${API.replace('/api', '')}${acc.logo_url}`} alt="" className="w-4 h-4 object-contain" />
                        )}
                        <span>{acc.name}</span>
                        {acc.logo_url && <span className="text-emerald-400 text-xs">(has logo)</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {uploadLogoAccount && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <p className="text-emerald-300 text-xs mb-2">
                  Selected: <span className="font-medium">{accounts.find(a => a.calldrip_account_id === uploadLogoAccount)?.name}</span>
                </p>
                {accounts.find(a => a.calldrip_account_id === uploadLogoAccount)?.logo_url ? (
                  <div className="flex items-center gap-3">
                    <img 
                      src={`${API.replace('/api', '')}${accounts.find(a => a.calldrip_account_id === uploadLogoAccount)?.logo_url}`} 
                      alt="Current logo" 
                      className="h-8 max-w-[100px] object-contain bg-slate-900 p-1 rounded"
                    />
                    <span className="text-slate-400 text-xs">Current logo (will be replaced)</span>
                  </div>
                ) : (
                  <p className="text-slate-400 text-xs">No logo uploaded yet</p>
                )}
              </div>
            )}
            
            <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600">
              <p className="text-slate-300 text-xs font-medium mb-1">Recommended:</p>
              <p className="text-slate-400 text-xs">
                PNG or JPG, transparent background preferred<br/>
                Max width: 200px for best display
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsUploadLogoDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => logoFileInputRef.current?.click()}
              disabled={uploadingLogo || !uploadLogoAccount}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {uploadingLogo ? "Uploading..." : "Select Image"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Hidden file input for logo upload */}
      <input
        ref={logoFileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUploadLogo}
        className="hidden"
      />
      
      {/* Hidden file input for edit dialog logo upload */}
      <input
        ref={editLogoInputRef}
        type="file"
        accept="image/*"
        onChange={handleUploadLogoFromEdit}
        className="hidden"
      />

      {/* Manage Reports Dialog */}
      <Dialog open={isManageReportsDialogOpen} onOpenChange={setIsManageReportsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText size={18} className="text-amber-400" />
              Manage Reports - {manageReportsAccount?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              View, set as current, or delete reports for this account.
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[400px] overflow-y-auto">
            {loadingReports ? (
              <div className="flex justify-center py-8">
                <div className="loading-spinner border-t-amber-500"></div>
              </div>
            ) : accountReports.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No reports found for this account.</p>
            ) : (
              <div className="space-y-2">
                {accountReports.map((report) => (
                  <div 
                    key={report.id} 
                    className={`p-3 rounded-lg border ${report.is_current ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-700/50 border-slate-600'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium text-sm truncate">{report.title}</p>
                          {report.is_current && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-slate-400 text-xs">{report.period}</p>
                        <p className="text-slate-500 text-[10px] mt-1">
                          {report.html_content ? '✓ Has content' : '⚠ No content (file-based)'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {!report.is_current && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSetCurrentReport(report.id)}
                            className="h-7 px-2 text-xs text-amber-400 hover:bg-amber-500/20"
                          >
                            Set Current
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteReport(report.id)}
                          disabled={deletingReportId === report.id}
                          className="h-7 px-2 text-xs text-red-400 hover:bg-red-500/20"
                        >
                          {deletingReportId === report.id ? '...' : <Trash2 size={14} />}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManageReportsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmUser} onOpenChange={() => setDeleteConfirmUser(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 size={18} className="text-red-400" />
              Delete User
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete this user? This action cannot be undone. All their data including scorecards and entries will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmUser(null)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteUser}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Org Visibility Management Dialog - Super Admin Only */}
      <Dialog open={isOrgVisibilityDialogOpen} onOpenChange={setIsOrgVisibilityDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield size={18} className="text-emerald-400" />
              Manage Org Access for {selectedUserForOrgs?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Toggle which organizations this admin can see and manage in the External Clients tab.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-2 max-h-80 overflow-y-auto">
            {allOrgsForVisibility.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No organizations found</p>
            ) : (
              allOrgsForVisibility.map(org => {
                const hasAccess = selectedUserForOrgs?.allowed_orgs?.includes(org.slug);
                return (
                  <div 
                    key={org.slug}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      hasAccess 
                        ? 'bg-emerald-500/10 border-emerald-500/50' 
                        : 'bg-slate-700/50 border-slate-600'
                    }`}
                  >
                    <div>
                      <p className="text-white font-medium">{org.name}</p>
                      <p className="text-slate-400 text-xs">{org.slug}</p>
                    </div>
                    <Switch
                      checked={hasAccess}
                      onCheckedChange={() => toggleOrgForUser(org.slug)}
                      disabled={savingOrgVisibility}
                      className={hasAccess ? 'data-[state=checked]:bg-emerald-500' : ''}
                    />
                  </div>
                );
              })
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOrgVisibilityDialogOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
